import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { format } from "date-fns";
import { CalendarDays, CheckCircle2, CreditCard, Loader2, Minus, Plus, ShoppingBag, UtensilsCrossed } from "lucide-react";
import { toast } from "sonner";
import { fmt } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { customer_name: "", email: "", phone: "", collection_date: "", collection_time: "", notes: "", website: "" };

export default function BuffetOrder({ onsite = false }) {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [cart, setCart] = useState({});
  const [voucher, setVoucher] = useState("");
  const [voucherInfo, setVoucherInfo] = useState(null);
  const [lookupEmail, setLookupEmail] = useState("");
  const [lookupLoading, setLookupLoading] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [dateOpen, setDateOpen] = useState(false);
  const [placed, setPlaced] = useState(null);
  const [paying, setPaying] = useState(false);
  const tries = useRef(0);
  const tableRef = searchParams.get("t") || "";

  useEffect(() => {
    axios.get(`${API}/public/hub/${slug}/order-menu`).then((r) => setData(r.data)).catch(() => setError("Ordering is unavailable for this business."));
  }, [slug]);

  // Returning from Stripe — confirm payment
  useEffect(() => {
    const oid = searchParams.get("order_id");
    const sid = searchParams.get("session_id");
    if (!oid) return;
    const poll = async () => {
      try {
        const { data: o } = await axios.get(`${API}/public/hub/${slug}/orders/${oid}`, { params: sid ? { session_id: sid } : {} });
        if (o.paid || tries.current > 8) {
          setPlaced({ order_id: oid, ...o });
          return;
        }
      } catch (e) { return; }
      tries.current += 1;
      setTimeout(poll, 2000);
    };
    poll();
  }, [searchParams, slug]);

  const total = useMemo(() => {
    if (!data) return 0;
    return Object.entries(cart).reduce((sum, [id, qty]) => {
      const item = data.items.find((i) => i.id === id);
      return sum + (item ? item.price * qty : 0);
    }, 0);
  }, [cart, data]);

  const upsellItems = (() => {
    if (!data) return [];
    const inCart = new Set(Object.keys(cart));
    const suggestions = new Set();
    for (const id of inCart) {
      for (const j of (data.also_bought?.[id] || [])) if (!inCart.has(j)) suggestions.add(j);
    }
    return [...suggestions].map((id) => data.items.find((i) => i.id === id)).filter(Boolean).slice(0, 4);
  })();

  const applyVoucher = async () => {
    if (!voucher.trim()) return;
    try {
      const { data: v } = await axios.post(`${API}/public/hub/${slug}/vouchers/validate`, { code: voucher });
      setVoucherInfo(v);
      setForm((f) => ({ ...f, voucher_code: v.code }));
      toast.success(`Voucher ${v.code} applied`);
    } catch {
      setVoucherInfo(null);
      toast.error("That code isn't valid");
    }
  };

  const doLookup = async () => {
    if (!lookupEmail) return;
    setLookupLoading(true);
    try {
      const { data: res } = await axios.post(`${API}/public/hub/${slug}/orders/lookup`, { email: lookupEmail });
      if (res.found) {
        const available = new Set(data.items.map((i) => i.id));
        const restored = {};
        for (const l of res.items) if (available.has(l.item_id)) restored[l.item_id] = l.qty;
        setCart(restored);
        setForm((f) => ({ ...f, email: lookupEmail }));
        toast.success("Last order loaded — review it and pick your date");
      } else {
        toast.info("No previous orders found for that email");
      }
    } catch {
      toast.error("Lookup failed — try again");
    } finally {
      setLookupLoading(false);
    }
  };

  const bump = (id, delta) => setCart((c) => {
    const next = { ...c, [id]: Math.max(0, (c[id] || 0) + delta) };
    if (!next[id]) delete next[id];
    return next;
  });

  if (error) return <div className="flex min-h-screen items-center justify-center bg-background"><p data-testid="order-error" className="border border-red-300 bg-red-50 px-6 py-4 text-sm text-red-700">{error}</p></div>;
  if (!data) return <div className="flex min-h-screen items-center justify-center bg-background"><Loader2 className="h-8 w-8 animate-spin text-brand" /></div>;

  const c = data.company;
  const accent = c.accent_color || "#059669";
  const dateBlocked = form.collection_date && (data.blocked_dates || []).includes(form.collection_date);
  const leadHours = Number(data.order_lead_time_hours || 0);
  const minDate = new Date(Date.now() + leadHours * 3600e3);
  const minDateOnly = new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate());
  const DAY_IDX = { sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6 };
  const closedDays = Object.entries(data.opening_hours || {}).filter(([, v]) => v && v.closed).map(([k]) => DAY_IDX[k]);
  const isClosedDay = (d) => closedDays.includes(d.getDay());
  const selectedDayHours = form.collection_date
    ? (data.opening_hours || {})[["sun", "mon", "tue", "wed", "thu", "fri", "sat"][new Date(form.collection_date + "T00:00:00").getDay()]]
    : null;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data: res } = await axios.post(`${API}/public/hub/${slug}/orders`, {
        order_type: onsite ? "onsite" : "pickup",
        ...form,
        table_ref: tableRef,
        items: Object.entries(cart).map(([item_id, qty]) => ({ item_id, qty })),
      });
      setPlaced(res);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not place order");
    } finally {
      setSubmitting(false);
    }
  };

  const payNow = async () => {
    setPaying(true);
    try {
      const { data: res } = await axios.post(`${API}/public/hub/${slug}/orders/${placed.order_id}/pay`, { origin_url: window.location.origin });
      window.location.href = res.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Unable to start payment");
      setPaying(false);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid={onsite ? "onsite-order-page" : "buffet-order-page"}>
      <header className="border-b border-white/10 bg-ink text-white">
        <div className="mx-auto flex max-w-4xl items-center gap-3 px-6 py-4">
          <div className="flex h-9 w-9 items-center justify-center" style={{ backgroundColor: accent }}><UtensilsCrossed className="h-5 w-5" /></div>
          <div>
            <p className="font-heading text-sm font-bold">{c.name}</p>
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/50">{onsite ? `On-site ordering${tableRef ? ` — ${tableRef}` : ""}` : "Buffet pickup ordering"}</p>
          </div>
        </div>
      </header>

      {!onsite && (
        <div className="mx-auto mt-6 max-w-4xl px-6">
          <div className="flex flex-wrap items-center gap-3 border border-border bg-white p-4" data-testid="reorder-box">
            <p className="text-sm font-semibold">Ordered before?</p>
            <Input type="email" placeholder="Your email" className="basis-full min-w-[12rem] sm:basis-auto sm:max-w-xs sm:flex-1" data-testid="reorder-email-input" value={lookupEmail} onChange={(e) => setLookupEmail(e.target.value)} />
            <Button type="button" variant="outline" className="rounded-md" data-testid="reorder-lookup-button" disabled={lookupLoading} onClick={doLookup}>{lookupLoading ? "Looking…" : "Load my last order"}</Button>
          </div>
        </div>
      )}

      <main className={`mx-auto max-w-4xl px-6 py-10 ${placed ? "flex min-h-[70vh] items-center" : ""}`}>
        {placed ? (
          <div className="mx-auto w-full max-w-lg border border-border bg-white p-8 text-center" data-testid="order-success">
            <CheckCircle2 className="mx-auto h-10 w-10" style={{ color: accent }} />
            <h1 className="mt-4 font-heading text-2xl font-bold">Order {placed.order_ref} {placed.paid ? "paid" : "placed"}</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              {(placed.order_type || (onsite ? "onsite" : "pickup")) === "onsite"
                ? `We'll bring it over${(placed.table_ref || tableRef) ? ` to ${placed.table_ref || tableRef}` : ""}.`
                : `Collect on ${placed.collection_date || form.collection_date} at ${placed.collection_time || form.collection_time}.`}
            </p>
            {placed.items && (
              <div className="mt-4 border border-border text-left text-sm">
                {placed.items.map((i) => (
                  <div key={i.item_id} className="flex justify-between border-b border-border px-4 py-2 last:border-0">
                    <span>{i.qty}× {i.name}</span><span className="tabular-nums">{fmt(i.line_total)}</span>
                  </div>
                ))}
              </div>
            )}
            {!placed.paid && !onsite && (
              <Button data-testid="order-pay-button" onClick={payNow} disabled={paying} className="mt-6 w-full rounded-md text-white" style={{ backgroundColor: accent }}>
                <CreditCard className="mr-2 h-4 w-4" /> {paying ? "Redirecting…" : `Pay now ${fmt(placed.total)} — or pay on collection`}
              </Button>
            )}
            {placed.paid && <p className="mt-4 text-sm font-semibold" style={{ color: accent }} data-testid="order-paid-note">Payment received — see you soon.</p>}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-5">
            <div className="space-y-3 md:col-span-3">
              <h1 className="font-heading text-2xl font-bold tracking-tight">{onsite ? "Order to your spot" : "Order buffets to collect"}</h1>
              {data.items.map((i) => (
                <div key={i.id} data-testid={`orderable-${i.id}`} className="flex items-center justify-between border border-border bg-white px-4 py-3">
                  <div>
                    <p className="text-sm font-semibold">{i.name}</p>
                    <p className="text-xs text-muted-foreground">{i.description} · serves {i.serves}</p>
                    <p className="mt-1 font-heading text-sm font-bold tabular-nums" style={{ color: accent }}>{fmt(i.price)}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button data-testid={`minus-${i.id}`} onClick={() => bump(i.id, -1)} className="flex h-8 w-8 items-center justify-center border border-border transition-colors duration-150 hover:bg-muted"><Minus className="h-4 w-4" /></button>
                    <span className="w-6 text-center font-bold tabular-nums" data-testid={`qty-${i.id}`}>{cart[i.id] || 0}</span>
                    <button data-testid={`plus-${i.id}`} onClick={() => bump(i.id, 1)} className="flex h-8 w-8 items-center justify-center border text-white" style={{ backgroundColor: accent }}><Plus className="h-4 w-4" /></button>
                  </div>
                </div>
              ))}
            </div>

            <form onSubmit={submit} className="space-y-4 border border-border bg-white p-5 md:col-span-2" data-testid="order-form">
              <div className="flex items-center justify-between border-b border-border pb-3">
                <p className="flex items-center gap-2 font-heading text-sm font-bold uppercase tracking-[0.15em]"><ShoppingBag className="h-4 w-4" /> Your order</p>
                <span className="font-heading text-lg font-black tabular-nums" data-testid="order-total">{fmt(total)}</span>
              </div>
              {!onsite && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" data-testid="hp-website" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }} />
                    <Label>Collection date</Label>
                    <Popover open={dateOpen} onOpenChange={setDateOpen}>
                      <PopoverTrigger asChild>
                        <Button type="button" variant="outline" data-testid="order-date-input" className="w-full justify-start text-left font-normal">
                          <CalendarDays className="mr-2 h-4 w-4" />
                          {form.collection_date ? format(new Date(form.collection_date + "T00:00:00"), "d MMM yyyy") : "Pick a date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto bg-white p-0" align="start">
                        <Calendar
                          mode="single"
                          initialFocus
                          selected={form.collection_date ? new Date(form.collection_date + "T00:00:00") : undefined}
                          onSelect={(d) => { if (d) { setForm({ ...form, collection_date: format(d, "yyyy-MM-dd") }); setDateOpen(false); } }}
                          disabled={[{ before: minDateOnly }, isClosedDay, ...(data.blocked_dates || []).map((bd) => new Date(bd + "T00:00:00"))]}
                        />
                      </PopoverContent>
                    </Popover>
                    {leadHours > 0 && <p className="text-xs text-muted-foreground" data-testid="order-lead-note">We need at least {leadHours}h notice — earliest collection is {format(minDate, "d MMM")}.</p>}
                    {dateBlocked && <p className="text-xs font-bold text-red-600" data-testid="order-date-blocked">Fully booked — pick another date.</p>}
                  </div>
                  <div className="space-y-1.5">
                    <Label>Time</Label>
                    <Input data-testid="order-time-input" type="time" required
                      min={selectedDayHours?.open || undefined} max={selectedDayHours?.close || undefined}
                      value={form.collection_time} onChange={(e) => setForm({ ...form, collection_time: e.target.value })} />
                    {selectedDayHours?.open && selectedDayHours?.close && (
                      <p className="text-xs text-muted-foreground" data-testid="order-hours-note">Open {selectedDayHours.open}–{selectedDayHours.close} that day.</p>
                    )}
                  </div>
                </div>
              )}
              <div className="space-y-1.5"><Label>Name</Label><Input data-testid="order-name-input" required value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Email</Label><Input data-testid="order-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Phone</Label><Input data-testid="order-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              {upsellItems.length > 0 && (
                <div className="border border-dashed border-border bg-muted/40 p-3" data-testid="upsell-row">
                  <p className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Customers also added</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {upsellItems.map((i) => (
                      <button key={i.id} type="button" data-testid={`upsell-add-${i.id}`} onClick={() => setCart({ ...cart, [i.id]: 1 })}
                        className="flex items-center gap-1.5 border border-border bg-white px-3 py-1.5 text-xs font-semibold transition-colors duration-150 hover:bg-muted">
                        <Plus className="h-3 w-3" /> {i.name} ({fmt(i.price)})
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div className="space-y-1.5"><Label>Notes</Label><Textarea data-testid="order-notes-input" rows={2} placeholder="Allergies, timing notes…" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
              <div className="flex gap-2">
                <Input placeholder="Voucher code" data-testid="voucher-input" value={voucher} onChange={(e) => setVoucher(e.target.value.toUpperCase())} className="font-mono" />
                <Button type="button" variant="outline" className="rounded-md" data-testid="voucher-apply-button" onClick={applyVoucher}>Apply</Button>
              </div>
              {voucherInfo && <p className="text-xs font-semibold text-emerald-700" data-testid="voucher-applied">{voucherInfo.code} applied — {voucherInfo.kind === "percent" ? `${voucherInfo.amount}%` : `£${voucherInfo.amount}`} off at checkout</p>}
              <Button data-testid="order-submit-button" type="submit" disabled={submitting || total <= 0 || dateBlocked} className="w-full rounded-md text-white" style={{ backgroundColor: accent }}>
                {submitting ? "Placing…" : onsite ? "Send to kitchen" : "Place order"}
              </Button>
              {onsite && <p className="text-center text-xs text-muted-foreground">Charged to your event tab.</p>}
            </form>
          </div>
        )}
      </main>
    </div>
  );
}
